import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';

const STEPS = [
  { id: 1, label: 'Business Info', icon: 'Building2' },
  { id: 2, label: 'Owners', icon: 'Users' },
  { id: 3, label: 'Bank Details', icon: 'Landmark' },
];

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan',
  'Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia',
  'Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica',
  'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt',
  'El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon',
  'Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana',
  'Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel',
  'Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos',
  'Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi',
  'Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova',
  'Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands',
  'New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan','Palau',
  'Palestine','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania',
  'Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino',
  'Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia',
  'Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan',
  'Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo',
  'Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates',
  'United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
];

const MCC_GROUPS = [
  {
    group: 'Agriculture & Veterinary',
    codes: [
      { code: '0742', description: 'Veterinary Services' },
      { code: '0763', description: 'Agricultural Cooperatives' },
      { code: '0780', description: 'Landscaping and Horticultural Services' },
    ],
  },
  {
    group: 'Construction & Contractors',
    codes: [
      { code: '1520', description: 'General Contractors - Residential and Commercial' },
      { code: '1711', description: 'Heating, Plumbing, and Air Conditioning' },
      { code: '1731', description: 'Electrical Contractors' },
      { code: '1740', description: 'Masonry, Stonework, Tile Setting, Plastering' },
      { code: '1750', description: 'Carpentry Contractors' },
      { code: '1761', description: 'Roofing, Siding, Sheet Metal Work' },
      { code: '1771', description: 'Concrete Work Contractors' },
      { code: '1799', description: 'Special Trade Contractors' },
    ],
  },
  {
    group: 'Publishing & Printing',
    codes: [
      { code: '2741', description: 'Miscellaneous Publishing and Printing' },
      { code: '2791', description: 'Typesetting, Plate Making, and Related Services' },
      { code: '2842', description: 'Specialty Cleaning, Polishing, and Sanitation Preparations' },
    ],
  },
  {
    group: 'Transportation & Logistics',
    codes: [
      { code: '4011', description: 'Railroads' },
      { code: '4111', description: 'Local and Suburban Commuter Passenger Transportation' },
      { code: '4112', description: 'Passenger Railways' },
      { code: '4119', description: 'Ambulance Services' },
      { code: '4121', description: 'Taxicabs and Limousines' },
      { code: '4131', description: 'Bus Lines' },
      { code: '4214', description: 'Motor Freight Carriers and Trucking' },
      { code: '4215', description: 'Courier Services - Air and Ground' },
      { code: '4225', description: 'Public Warehousing and Storage' },
      { code: '4784', description: 'Tolls and Bridge Fees' },
      { code: '4789', description: 'Transportation Services' },
    ],
  },
  {
    group: 'Travel & Tourism',
    codes: [
      { code: '4411', description: 'Cruise Lines' },
      { code: '4457', description: 'Boat Rentals and Leases' },
      { code: '4468', description: 'Marinas, Marine Service, and Supplies' },
      { code: '4511', description: 'Airlines, Air Carriers' },
      { code: '4582', description: 'Airports, Flying Fields' },
      { code: '4722', description: 'Travel Agencies and Tour Operators' },
      { code: '4723', description: 'Package Tour Operators' },
    ],
  },
  {
    group: 'Telecommunications & Utilities',
    codes: [
      { code: '4812', description: 'Telecommunication Equipment and Telephone Sales' },
      { code: '4813', description: 'Telephone Services' },
      { code: '4814', description: 'Fax Services' },
      { code: '4816', description: 'Computer Network/Information Services' },
      { code: '4821', description: 'Telegraph Services' },
      { code: '4829', description: 'Wire Transfers and Money Orders' },
      { code: '4899', description: 'Cable, Satellite, and Other Pay Television and Radio' },
      { code: '4900', description: 'Utilities - Electric, Gas, Water, Sanitary' },
    ],
  },
  {
    group: 'Wholesale & Industrial',
    codes: [
      { code: '5013', description: 'Motor Vehicle Supplies and New Parts' },
      { code: '5021', description: 'Office and Commercial Furniture' },
      { code: '5039', description: 'Construction Materials' },
      { code: '5044', description: 'Photographic, Photocopy, Microfilm Equipment and Supplies' },
      { code: '5045', description: 'Computers, Peripherals, and Software' },
      { code: '5046', description: 'Commercial Equipment' },
      { code: '5047', description: 'Medical and Dental Equipment and Supplies' },
      { code: '5051', description: 'Metal Service Centers and Offices' },
      { code: '5065', description: 'Electrical Parts and Equipment' },
      { code: '5072', description: 'Hardware Equipment and Supplies' },
      { code: '5074', description: 'Plumbing and Heating Equipment and Supplies' },
      { code: '5085', description: 'Industrial and Commercial Machinery and Equipment' },
      { code: '5094', description: 'Jewelry, Watches, Clocks, and Silverware' },
      { code: '5099', description: 'Durable Goods' },
      { code: '5111', description: 'Stationery, Office Supplies, Printing and Writing Paper' },
      { code: '5122', description: 'Drugs, Drug Proprietaries and Druggists Sundries' },
      { code: '5131', description: 'Piece Goods, Notions, and Dry Goods' },
      { code: '5137', description: 'Uniforms and Commercial Clothing' },
      { code: '5139', description: 'Commercial Footwear' },
      { code: '5169', description: 'Chemicals and Allied Products' },
      { code: '5172', description: 'Petroleum and Petroleum Products' },
      { code: '5192', description: 'Books, Periodicals, and Newspapers' },
      { code: '5193', description: 'Florists Supplies, Nursery Stock, and Flowers' },
      { code: '5198', description: 'Paints, Varnishes, and Supplies' },
      { code: '5199', description: 'Nondurable Goods' },
    ],
  },
  {
    group: 'Home Improvement & Hardware',
    codes: [
      { code: '5200', description: 'Home Supply Warehouse Stores' },
      { code: '5211', description: 'Lumber, Building Material Stores' },
      { code: '5231', description: 'Glass, Paint, Wallpaper Stores' },
      { code: '5251', description: 'Hardware Stores' },
      { code: '5261', description: 'Nurseries, Lawn and Garden Supply Stores' },
      { code: '5271', description: 'Mobile Home Dealers' },
    ],
  },
  {
    group: 'General Retail & Department Stores',
    codes: [
      { code: '5300', description: 'Wholesale Clubs' },
      { code: '5309', description: 'Duty Free Stores' },
      { code: '5310', description: 'Discount Stores' },
      { code: '5311', description: 'Department Stores' },
      { code: '5331', description: 'Variety Stores' },
      { code: '5399', description: 'Miscellaneous General Merchandise' },
    ],
  },
  {
    group: 'Food & Grocery',
    codes: [
      { code: '5411', description: 'Grocery Stores, Supermarkets' },
      { code: '5422', description: 'Freezer and Locker Meat Provisioners' },
      { code: '5441', description: 'Candy, Nut, and Confectionery Stores' },
      { code: '5451', description: 'Dairy Products Stores' },
      { code: '5462', description: 'Bakeries' },
      { code: '5499', description: 'Miscellaneous Food Stores' },
    ],
  },
  {
    group: 'Automotive',
    codes: [
      { code: '5511', description: 'Car and Truck Dealers (New and Used)' },
      { code: '5521', description: 'Car and Truck Dealers (Used Only)' },
      { code: '5531', description: 'Auto and Home Supply Stores' },
      { code: '5532', description: 'Automotive Tire Stores' },
      { code: '5533', description: 'Automotive Parts and Accessories Stores' },
      { code: '5541', description: 'Service Stations' },
      { code: '5542', description: 'Automated Fuel Dispensers' },
      { code: '5551', description: 'Boat Dealers' },
      { code: '5561', description: 'Camper, Recreational and Utility Trailer Dealers' },
      { code: '5571', description: 'Motorcycle Shops and Dealers' },
      { code: '5592', description: 'Motor Home Dealers' },
      { code: '5598', description: 'Snowmobile Dealers' },
      { code: '5599', description: 'Miscellaneous Automotive Dealers' },
    ],
  },
  {
    group: 'Clothing & Apparel',
    codes: [
      { code: '5611', description: "Men\'s and Boys\' Clothing and Accessories Stores" },
      { code: '5621', description: "Women\'s Ready-to-Wear Stores" },
      { code: '5631', description: "Women\'s Accessory and Specialty Shops" },
      { code: '5641', description: "Children\'s and Infants\' Wear Stores" },
      { code: '5651', description: 'Family Clothing Stores' },
      { code: '5655', description: 'Sports and Riding Apparel Stores' },
      { code: '5661', description: 'Shoe Stores' },
      { code: '5681', description: 'Furriers and Fur Shops' },
      { code: '5691', description: "Men\'s and Women\'s Clothing Stores" },
      { code: '5697', description: 'Tailors, Seamstresses, Mending, and Alterations' },
      { code: '5698', description: 'Wig and Toupee Stores' },
      { code: '5699', description: 'Miscellaneous Apparel and Accessory Shops' },
    ],
  },
  {
    group: 'Home Furnishings & Electronics',
    codes: [
      { code: '5712', description: 'Furniture, Home Furnishings, and Equipment Stores' },
      { code: '5713', description: 'Floor Covering Stores' },
      { code: '5714', description: 'Drapery, Window Covering, and Upholstery Stores' },
      { code: '5718', description: 'Fireplace, Fireplace Screens, and Accessories Stores' },
      { code: '5719', description: 'Miscellaneous Home Furnishing Specialty Stores' },
      { code: '5722', description: 'Household Appliance Stores' },
      { code: '5731', description: 'Electronics Stores' },
      { code: '5732', description: 'Electronics Sales' },
      { code: '5733', description: 'Music Stores - Musical Instruments, Pianos, and Sheet Music' },
      { code: '5734', description: 'Computer Software Stores' },
      { code: '5735', description: 'Record Stores' },
    ],
  },
  {
    group: 'Restaurants & Food Service',
    codes: [
      { code: '5811', description: 'Caterers' },
      { code: '5812', description: 'Eating Places and Restaurants' },
      { code: '5813', description: 'Drinking Places (Alcoholic Beverages)' },
      { code: '5814', description: 'Fast Food Restaurants' },
    ],
  },
  {
    group: 'Health & Pharmacy',
    codes: [
      { code: '5912', description: 'Drug Stores and Pharmacies' },
      { code: '5921', description: 'Package Stores - Beer, Wine, and Liquor' },
    ],
  },
  {
    group: 'Specialty & Hobby Retail',
    codes: [
      { code: '5931', description: 'Used Merchandise and Secondhand Stores' },
      { code: '5932', description: 'Antique Shops' },
      { code: '5933', description: 'Pawn Shops' },
      { code: '5935', description: 'Wrecking and Salvage Yards' },
      { code: '5937', description: 'Antique Reproductions' },
      { code: '5940', description: 'Sporting Goods Stores and Bicycle Shops' },
      { code: '5941', description: 'Sporting Goods Stores' },
      { code: '5942', description: 'Book Stores' },
      { code: '5943', description: 'Stationery Stores, Office and School Supply Stores' },
      { code: '5944', description: 'Jewelry Stores, Watches, Clocks, and Silverware Stores' },
      { code: '5945', description: 'Hobby, Toy, and Game Shops' },
      { code: '5946', description: 'Camera and Photographic Supply Stores' },
      { code: '5947', description: 'Gift, Card, Novelty, and Souvenir Shops' },
      { code: '5948', description: 'Luggage and Leather Goods Stores' },
      { code: '5949', description: 'Sewing, Needlework, Fabric, and Piece Goods Stores' },
      { code: '5950', description: 'Glassware, Crystal Stores' },
      { code: '5970', description: "Artist\'s Supply and Craft Shops" },
      { code: '5971', description: 'Art Dealers and Galleries' },
      { code: '5972', description: 'Stamp and Coin Stores' },
      { code: '5973', description: 'Religious Goods Stores' },
      { code: '5975', description: 'Hearing Aids Sales and Supplies' },
      { code: '5976', description: 'Orthopedic Goods - Prosthetic Devices' },
      { code: '5977', description: 'Cosmetic Stores' },
      { code: '5978', description: 'Typewriter Stores' },
      { code: '5983', description: 'Fuel Dealers' },
      { code: '5992', description: 'Florists' },
      { code: '5993', description: 'Cigar Stores and Stands' },
      { code: '5994', description: 'News Dealers and Newsstands' },
      { code: '5995', description: 'Pet Shops, Pet Food, and Supplies' },
      { code: '5996', description: 'Swimming Pools - Sales' },
      { code: '5997', description: 'Electric Razor Stores' },
      { code: '5998', description: 'Tent and Awning Shops' },
      { code: '5999', description: 'Miscellaneous and Specialty Retail Stores' },
    ],
  },
  {
    group: 'Direct Marketing',
    codes: [
      { code: '5960', description: 'Direct Marketing - Insurance Services' },
      { code: '5961', description: 'Catalog and Mail-Order Houses' },
      { code: '5962', description: 'Telemarketing' },
      { code: '5963', description: 'Door-to-Door Sales' },
      { code: '5964', description: 'Direct Marketing - Catalog Merchant' },
      { code: '5965', description: 'Direct Marketing - Combination Catalog and Retail Merchant' },
      { code: '5966', description: 'Direct Marketing - Outbound Telemarketing Merchant' },
      { code: '5967', description: 'Direct Marketing - Inbound Teleservices Merchant' },
      { code: '5968', description: 'Direct Marketing - Continuity/Subscription Merchant' },
      { code: '5969', description: 'Direct Marketing - Other Direct Marketers' },
    ],
  },
  {
    group: 'Financial Services',
    codes: [
      { code: '6010', description: 'Manual Cash Disbursements' },
      { code: '6011', description: 'Automated Cash Disbursements' },
      { code: '6012', description: 'Merchandise and Services' },
      { code: '6051', description: 'Non-Financial Institutions - Foreign Currency, Money Orders' },
      { code: '6099', description: 'Financial Institutions - Manual Cash Disbursements' },
      { code: '6211', description: 'Security Brokers/Dealers' },
      { code: '6300', description: 'Insurance Sales, Underwriting, and Premiums' },
      { code: '6381', description: 'Insurance Premiums' },
      { code: '6399', description: 'Insurance - Not Elsewhere Classified' },
      { code: '6513', description: 'Real Estate Agents and Managers - Rentals' },
    ],
  },
  {
    group: 'Lodging & Accommodation',
    codes: [
      { code: '7011', description: 'Lodging - Hotels, Motels, Resorts' },
      { code: '7012', description: 'Timeshares' },
      { code: '7032', description: 'Sporting and Recreational Camps' },
      { code: '7033', description: 'Trailer Parks, Campgrounds' },
      { code: '7041', description: 'Membership Clubs (Sports, Recreation, Athletic)' },
    ],
  },
  {
    group: 'Personal Services',
    codes: [
      { code: '7210', description: 'Laundry, Cleaning, Garment Services' },
      { code: '7211', description: 'Laundries - Family and Commercial' },
      { code: '7216', description: 'Dry Cleaners' },
      { code: '7217', description: 'Carpet and Upholstery Cleaning' },
      { code: '7221', description: 'Photographic Studios' },
      { code: '7230', description: 'Barber and Beauty Shops' },
      { code: '7251', description: 'Shoe Repair Shops, Shoe Shine Parlors, and Hat Cleaning Shops' },
      { code: '7261', description: 'Funeral Services and Crematories' },
      { code: '7273', description: 'Dating and Escort Services' },
      { code: '7276', description: 'Tax Preparation Services' },
      { code: '7277', description: 'Counseling Services - Debt, Marriage, Personal' },
      { code: '7278', description: 'Buying/Shopping Services, Clubs' },
      { code: '7296', description: 'Clothing Rental - Costumes, Uniforms, Formal Wear' },
      { code: '7297', description: 'Massage Parlors' },
      { code: '7298', description: 'Health and Beauty Spas' },
      { code: '7299', description: 'Miscellaneous Personal Services' },
    ],
  },
  {
    group: 'Business Services',
    codes: [
      { code: '7311', description: 'Advertising Services' },
      { code: '7321', description: 'Consumer Credit Reporting Agencies' },
      { code: '7333', description: 'Commercial Photography, Art, and Graphics' },
      { code: '7338', description: 'Quick Copy, Reproduction, and Blueprinting Services' },
      { code: '7339', description: 'Stenographic and Secretarial Support Services' },
      { code: '7342', description: 'Exterminating and Disinfecting Services' },
      { code: '7349', description: 'Building Cleaning and Maintenance Services' },
      { code: '7361', description: 'Help Supply Services' },
      { code: '7372', description: 'Computer Programming, Data Processing' },
      { code: '7374', description: 'Computer Processing and Data Preparation' },
      { code: '7375', description: 'Information Retrieval Services' },
      { code: '7379', description: 'Computer Maintenance and Repair' },
      { code: '7392', description: 'Management, Consulting, and Public Relations Services' },
      { code: '7393', description: 'Detective Agencies, Protective Services, and Security Services' },
      { code: '7394', description: 'Equipment Rental and Leasing Services' },
      { code: '7395', description: 'Photofinishing Laboratories, Photo Developing' },
      { code: '7399', description: 'Business Services' },
    ],
  },
  {
    group: 'Automotive Services & Repair',
    codes: [
      { code: '7512', description: 'Car Rental Agencies' },
      { code: '7513', description: 'Truck and Utility Trailer Rentals' },
      { code: '7519', description: 'Motor Home and Recreational Vehicle Rentals' },
      { code: '7523', description: 'Parking Lots, Parking Meters, and Garages' },
      { code: '7531', description: 'Auto Body Repair Shops' },
      { code: '7534', description: 'Tire Retreading and Repair Shops' },
      { code: '7535', description: 'Auto Paint Shops' },
      { code: '7538', description: 'Automotive Service Shops' },
      { code: '7542', description: 'Car Washes' },
      { code: '7549', description: 'Towing Services' },
    ],
  },
  {
    group: 'Repair Services',
    codes: [
      { code: '7622', description: 'Electronics Repair Shops' },
      { code: '7623', description: 'Air Conditioning and Refrigeration Repair Shops' },
      { code: '7629', description: 'Electrical and Small Appliance Repair Shops' },
      { code: '7631', description: 'Watch, Clock, and Jewelry Repair' },
      { code: '7641', description: 'Furniture Repair, Refinishing' },
      { code: '7692', description: 'Welding Repair' },
      { code: '7699', description: 'Miscellaneous Repair Shops and Related Services' },
    ],
  },
  {
    group: 'Gambling & Gaming',
    codes: [
      { code: '7800', description: 'Government-Licensed Horse/Dog Racing' },
      { code: '7801', description: 'Government-Licensed Casinos' },
      { code: '7802', description: 'Government-Owned Lotteries' },
      { code: '7995', description: 'Betting/Casino Gambling' },
    ],
  },
  {
    group: 'Entertainment & Recreation',
    codes: [
      { code: '7911', description: 'Dance Halls, Studios, and Schools' },
      { code: '7922', description: 'Theatrical Ticket Agencies' },
      { code: '7929', description: 'Bands, Orchestras, Actors, and Other Entertainers' },
      { code: '7932', description: 'Billiard and Pool Establishments' },
      { code: '7933', description: 'Bowling Alleys' },
      { code: '7941', description: 'Commercial Sports, Athletic Fields, Professional Sport Clubs' },
      { code: '7991', description: 'Tourist Attractions and Exhibits' },
      { code: '7992', description: 'Golf Courses - Public' },
      { code: '7993', description: 'Video Amusement Game Supplies' },
      { code: '7994', description: 'Video Game Arcades/Establishments' },
      { code: '7996', description: 'Amusement Parks, Carnivals, Circuses, Fortune Tellers' },
      { code: '7997', description: 'Membership Clubs' },
      { code: '7998', description: 'Aquariums, Seaquariums, Dolphinariums' },
      { code: '7999', description: 'Recreation Services' },
    ],
  },
  {
    group: 'Healthcare & Medical',
    codes: [
      { code: '8011', description: 'Doctors and Physicians' },
      { code: '8021', description: 'Dentists and Orthodontists' },
      { code: '8031', description: 'Osteopaths' },
      { code: '8041', description: 'Chiropractors' },
      { code: '8042', description: 'Optometrists and Ophthalmologists' },
      { code: '8043', description: 'Opticians, Optical Goods, and Eyeglasses' },
      { code: '8049', description: 'Podiatrists and Chiropodists' },
      { code: '8050', description: 'Nursing and Personal Care Facilities' },
      { code: '8062', description: 'Hospitals' },
      { code: '8071', description: 'Medical and Dental Laboratories' },
      { code: '8099', description: 'Health Practitioners, Medical Services' },
    ],
  },
  {
    group: 'Legal & Professional Services',
    codes: [
      { code: '8111', description: 'Legal Services and Attorneys' },
      { code: '8711', description: 'Engineering Services' },
      { code: '8712', description: 'Architectural Services' },
      { code: '8713', description: 'Surveying Services' },
      { code: '8721', description: 'Accounting, Auditing, and Bookkeeping Services' },
      { code: '8731', description: 'Commercial Research' },
      { code: '8734', description: 'Testing Laboratories' },
      { code: '8742', description: 'Management Consulting Services' },
      { code: '8743', description: 'Public Relations Services' },
      { code: '8744', description: 'Facilities Support Management Services' },
      { code: '8748', description: 'Business Consulting Services' },
      { code: '8911', description: 'Architectural and Engineering Services' },
      { code: '8931', description: 'Accounting, Auditing, and Bookkeeping Services' },
      { code: '8999', description: 'Services - Not Elsewhere Classified' },
    ],
  },
  {
    group: 'Education',
    codes: [
      { code: '8211', description: 'Elementary and Secondary Schools' },
      { code: '8220', description: 'Colleges, Universities, Professional Schools' },
      { code: '8241', description: 'Correspondence Schools' },
      { code: '8244', description: 'Business and Secretarial Schools' },
      { code: '8249', description: 'Vocational and Trade Schools' },
      { code: '8299', description: 'Schools and Educational Services' },
      { code: '8351', description: 'Child Care Services' },
    ],
  },
  {
    group: 'Non-Profit & Organizations',
    codes: [
      { code: '8398', description: 'Charitable and Social Service Organizations' },
      { code: '8641', description: 'Civic, Social, Fraternal Associations' },
      { code: '8651', description: 'Political Organizations' },
      { code: '8661', description: 'Religious Organizations' },
      { code: '8675', description: 'Automobile Associations' },
      { code: '8699', description: 'Membership Organizations' },
    ],
  },
  {
    group: 'Government & Public Services',
    codes: [
      { code: '9211', description: 'Court Costs, Including Alimony and Child Support' },
      { code: '9222', description: 'Fines' },
      { code: '9223', description: 'Bail and Bond Payments' },
      { code: '9311', description: 'Tax Payments' },
      { code: '9399', description: 'Government Services' },
      { code: '9402', description: 'Postal Services - Government Only' },
      { code: '9405', description: 'Intra-Government Purchases' },
    ],
  },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedMccGroup, setSelectedMccGroup] = useState('');

  const registrationId = user?.registration_id || user?.id;

  // Step 1 - Business Info
  const [businessForm, setBusinessForm] = useState({
    business_name: '', business_type: '', license_type: '',
    registration_number: '', address: '', city: '', country: '', postal_code: '',
    website: '', phone: '', email: '',
  });
  const [businessDocs, setBusinessDocs] = useState([]);

  // Step 2 - Owners
  const [owners, setOwners] = useState([{
    first_name: '', last_name: '', email: '', phone: '', dob: '',
    nationality: '', id_number: '', ownership_percentage: '',
    id_doc: null, passport_photo: null, proof_of_address: null,
  }]);

  // Step 3 - Bank Details
  const [bankForm, setBankForm] = useState({
    bank_name: '', account_number: '', account_name: '', branch: '',
    swift_code: '', iban: '', currency: '',
  });
  const [bankStatement, setBankStatement] = useState(null);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    if (!registrationId) { setLoading(false); return; }
    try {
      const response = await apiService?.getOnboardingStatus(registrationId);
      const data = response?.data;
      setOnboardingStatus(data);
      const lastStep = data?.last_completed_step || data?.current_step || 0;
      const nextStep = Math.min(lastStep + 1, 3);
      setCurrentStep(nextStep);
      const completed = [];
      for (let i = 1; i <= lastStep; i++) completed?.push(i);
      setCompletedSteps(completed);
    } catch (err) {
      // New user, start from step 1
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setError('');
    try {
      const formData = new FormData();
      Object.entries(businessForm)?.forEach(([k, v]) => formData?.append(k, v));
      businessDocs?.forEach(doc => formData?.append('documents', doc));
      await apiService?.submitBusinessInfo(registrationId, formData);
      setCompletedSteps(prev => [...new Set([...prev, 1])]);
      setCurrentStep(2);
      setSuccess('Business information saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save business information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOwnersSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setError('');
    try {
      const formData = new FormData();
      owners?.forEach((owner, i) => {
        Object.entries(owner)?.forEach(([k, v]) => {
          if (v instanceof File) formData?.append(`owners[${i}][${k}]`, v);
          else if (v !== null) formData?.append(`owners[${i}][${k}]`, v);
        });
      });
      await apiService?.submitOwners(registrationId, formData);
      setCompletedSteps(prev => [...new Set([...prev, 2])]);
      setCurrentStep(3);
      setSuccess('Owners information saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save owners information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true); setError('');
    try {
      const formData = new FormData();
      Object.entries(bankForm)?.forEach(([k, v]) => formData?.append(k, v));
      if (bankStatement) formData?.append('bank_statement', bankStatement);
      await apiService?.submitBankDetails(registrationId, formData);
      setCompletedSteps(prev => [...new Set([...prev, 3])]);
      setSuccess('Bank details saved! Your application is under review.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save bank details');
    } finally {
      setSubmitting(false);
    }
  };

  const addOwner = () => setOwners(prev => [...prev, {
    first_name: '', last_name: '', email: '', phone: '', dob: '',
    nationality: '', id_number: '', ownership_percentage: '',
    id_doc: null, passport_photo: null, proof_of_address: null,
  }]);

  const updateOwner = (index, field, value) => {
    setOwners(prev => prev?.map((o, i) => i === index ? { ...o, [field]: value } : o));
  };

  const inputClass = "w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
  const labelClass = "block text-sm font-medium text-text-primary mb-1";

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="flex items-center justify-center h-96">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  if (onboardingStatus?.completion_status === 'APPROVED') {
    return (
      <div className="min-h-screen bg-background">
        <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
          <div className="p-6 flex items-center justify-center">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Application Approved!</h2>
              <p className="text-text-secondary">Your business has been approved. You can now access all features.</p>
              <button onClick={() => navigate('/dashboard')} className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90">
                Go to Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6 max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">Business Onboarding</h1>
            <p className="text-text-secondary mt-1">Complete all steps to activate your merchant account</p>
          </div>

          {/* Status Banner */}
          {onboardingStatus?.completion_status && onboardingStatus?.completion_status !== 'APPROVED' && (
            <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-warning" />
              <span className="text-sm text-warning">Status: {onboardingStatus?.completion_status}</span>
            </div>
          )}

          {/* Step Indicator */}
          <div className="flex items-center mb-8">
            {STEPS?.map((step, i) => (
              <React.Fragment key={step?.id}>
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => completedSteps?.includes(step?.id) && setCurrentStep(step?.id)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-smooth ${
                      currentStep === step?.id ? 'bg-primary text-primary-foreground' :
                      completedSteps?.includes(step?.id) ? 'bg-success text-success-foreground': 'bg-muted text-text-secondary'
                    }`}
                  >
                    {completedSteps?.includes(step?.id) && currentStep !== step?.id ? (
                      <Icon name="Check" size={16} />
                    ) : step?.id}
                  </button>
                  <span className={`text-xs mt-1 font-medium ${
                    currentStep === step?.id ? 'text-primary' : 'text-text-secondary'
                  }`}>{step?.label}</span>
                </div>
                {i < STEPS?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mb-4 ${
                    completedSteps?.includes(step?.id) ? 'bg-success' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Step 1: Business Info */}
          {currentStep === 1 && (
            <form onSubmit={handleBusinessSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                <Icon name="Building2" size={20} className="text-primary" />
                <span>Business Information</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['business_name','Business Name'],['registration_number','Registration Number'],['business_type','Business Type'],['license_type','License Type'],['website','Website'],['phone','Phone'],['email','Email'],['address','Address'],['city','City'],['country','Country'],['postal_code','Postal Code']]?.map(([name, label]) => (
                  <div key={name}>
                    <label className={labelClass}>{label}</label>
                    {name === 'country' ? (
                      <select value={businessForm?.country} onChange={e => setBusinessForm(p => ({ ...p, country: e?.target?.value }))} className={inputClass}>
                        <option value="">Select Country</option>
                        {COUNTRIES?.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={businessForm?.[name]} onChange={e => setBusinessForm(p => ({ ...p, [name]: e?.target?.value }))}
                        placeholder={label} className={inputClass} />
                    )}
                  </div>
                ))}
              </div>
              {/* Industry: Group + MCC two-step dropdowns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Industry Group</label>
                  <select
                    value={selectedMccGroup}
                    onChange={e => {
                      setSelectedMccGroup(e?.target?.value);
                      setBusinessForm(p => ({ ...p, industry: '' }));
                    }}
                    className={inputClass}
                  >
                    <option value="">Select Industry Group</option>
                    {MCC_GROUPS?.map(g => (
                      <option key={g?.group} value={g?.group}>{g?.group}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Industry (MCC)</label>
                  <select
                    value={businessForm?.industry}
                    onChange={e => setBusinessForm(p => ({ ...p, industry: e?.target?.value }))}
                    className={inputClass}
                    disabled={!selectedMccGroup}
                  >
                    <option value="">{selectedMccGroup ? 'Select MCC Code' : 'Select a group first'}</option>
                    {(MCC_GROUPS?.find(g => g?.group === selectedMccGroup)?.codes || [])?.map(m => (
                      <option key={m?.code} value={m?.code}>{m?.code} - {m?.description}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Business Documents</label>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setBusinessDocs(Array.from(e?.target?.files))}
                  className="w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-sm file:cursor-pointer" />
                {businessDocs?.length > 0 && <p className="text-xs text-text-secondary mt-1">{businessDocs?.length} file(s) selected</p>}
              </div>
              <div className="flex justify-end">
                <button type="submit" disabled={submitting}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Saving...</span></> : <><span>Save & Continue</span><Icon name="ArrowRight" size={14} /></>}
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Owners */}
          {currentStep === 2 && (
            <form onSubmit={handleOwnersSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                  <Icon name="Users" size={20} className="text-primary" />
                  <span>Owners Information</span>
                </h2>
                <button type="button" onClick={addOwner}
                  className="flex items-center space-x-1 px-3 py-1.5 border border-primary text-primary rounded-lg text-sm hover:bg-primary/10">
                  <Icon name="Plus" size={14} /><span>Add Owner</span>
                </button>
              </div>
              {owners?.map((owner, i) => (
                <div key={i} className="p-4 border border-border rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-text-primary">Owner {i + 1}</h3>
                    {owners?.length > 1 && (
                      <button type="button" onClick={() => setOwners(prev => prev?.filter((_, idx) => idx !== i))}
                        className="text-error hover:text-error/80">
                        <Icon name="Trash2" size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[['first_name','First Name'],['last_name','Last Name'],['email','Email'],['phone','Phone'],['dob','Date of Birth'],['nationality','Nationality'],['id_number','ID Number'],['ownership_percentage','Ownership %']]?.map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        <input type={field === 'dob' ? 'date' : 'text'} value={owner?.[field]}
                          onChange={e => updateOwner(i, field, e?.target?.value)}
                          placeholder={label} className={inputClass} />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[['id_doc','ID Document'],['passport_photo','Passport Photo'],['proof_of_address','Proof of Address']]?.map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                          onChange={e => updateOwner(i, field, e?.target?.files?.[0])}
                          className="w-full text-xs text-text-secondary file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-muted file:text-text-primary file:text-xs file:cursor-pointer" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-between">
                <button type="button" onClick={() => setCurrentStep(1)}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted flex items-center space-x-2">
                  <Icon name="ArrowLeft" size={14} /><span>Back</span>
                </button>
                <button type="submit" disabled={submitting}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Saving...</span></> : <><span>Save & Continue</span><Icon name="ArrowRight" size={14} /></>}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Bank Details */}
          {currentStep === 3 && (
            <form onSubmit={handleBankSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
                <Icon name="Landmark" size={20} className="text-primary" />
                <span>Bank Details</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['bank_name','Bank Name'],['account_number','Account Number'],['account_name','Account Name'],['branch','Branch'],['swift_code','SWIFT Code'],['iban','IBAN'],['currency','Currency']]?.map(([name, label]) => (
                  <div key={name}>
                    <label className={labelClass}>{label}</label>
                    <input type="text" value={bankForm?.[name]} onChange={e => setBankForm(p => ({ ...p, [name]: e?.target?.value }))}
                      placeholder={label} className={inputClass} />
                  </div>
                ))}
              </div>
              <div>
                <label className={labelClass}>Bank Statement</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setBankStatement(e?.target?.files?.[0])}
                  className="w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-sm file:cursor-pointer" />
              </div>
              <div className="flex justify-between">
                <button type="button" onClick={() => setCurrentStep(2)}
                  className="px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted flex items-center space-x-2">
                  <Icon name="ArrowLeft" size={14} /><span>Back</span>
                </button>
                <button type="submit" disabled={submitting}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center space-x-2">
                  {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Submitting...</span></> : <><span>Submit Application</span><Icon name="Send" size={14} /></>}
                </button>
              </div>
            </form>
          )}

          {/* All steps completed */}
          {completedSteps?.length === 3 && currentStep > 3 && (
            <div className="bg-surface border border-border rounded-xl p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Application Submitted!</h2>
              <p className="text-text-secondary">Your application is under review. We'll notify you once it's processed.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OnboardingPage;
